import classNames from 'classnames'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'secondary-ai'
  | 'tertiary'
  | 'haze'
  | 'quiet'
  | 'destructive'
  | 'highlighted'
  | 'purple'
  | 'custom'

export const buttonVariantsClasses: Record<
  ButtonVariant,
  { base: string; default: string; disabled: string; isActive: string }
> = {
  primary: {
    base: 'font-weight-extrabold white border-small border-transparent',
    default:
      'background-primary background-primary-hover-on-hover background-primary-active-on-active',
    disabled: 'background-alpha-shade-55 shade-30',
    isActive: 'background-blue-20 background-blue-40-on-dark-mode',
  },
  secondary: {
    base: 'font-weight-semibold border-small border-transparent',
    default: classNames(
      'shade-10 background-alpha-shade-55',
      'background-alpha-shade-45-on-hover background-alpha-shade-45-on-active'
    ),
    disabled: 'shade-30 background-alpha-shade-55',
    isActive: 'shade-10 background-alpha-shade-45',
  },
  'secondary-ai': {
    base: 'font-weight-semibold border-small border-transparent',
    default: classNames(
      'purple-10 background-alpha-purple-50',
      'background-alpha-purple-45-on-hover background-alpha-purple-40-on-active'
    ),
    disabled: 'shade-30 background-alpha-shade-55',
    isActive: 'purple-10 background-alpha-shade-40',
  },
  tertiary: {
    base: 'font-weight-semibold border-small border-transparent',
    default: classNames(
      'shade-10 background-transparent',
      'background-alpha-shade-50-on-hover background-alpha-shade-45-on-active'
    ),
    disabled: 'shade-40 background-transparent',
    isActive: 'background-shade-45 border-shade-45 shade-20',
  },
  // same as tertiary but with lighter text color
  quiet: {
    base: 'font-weight-semibold border-small border-transparent',
    default: classNames(
      'shade-30 background-transparent border-small',
      'background-shade-50-on-hover shade-10-on-hover',
      'background-shade-45-on-active shade-10-on-active'
    ),
    disabled: 'shade-40 background-transparent',
    isActive: 'background-shade-45 border-shade-45 shade-10',
  },
  haze: {
    base: 'border-none',
    default: classNames(
      'shade-100 shade-100-on-hover background-alpha-shade-40',
      'background-alpha-shade-30-on-hover background-alpha-shade-20-on-active'
    ),
    disabled: 'shade-40 background-transparent',
    isActive: 'background-alpha-shade-20',
  },
  destructive: {
    base: 'font-weight-semibold border-none',
    default: classNames(
      'red-40 background-alpha-red-50',
      'background-alpha-red-45-on-hover background-alpha-red-40-on-active'
    ),
    disabled: 'background-alpha-shade-55 shade-30',
    isActive: 'background-alpha-red-40 red-40',
  },
  highlighted: {
    base: 'font-weight-semibold border-small border-transparent',
    default: classNames(
      'blue-30 background-alpha-blue-50 background-alpha-blue-45-on-hover',
      'blue-20-on-dark-mode'
    ),
    disabled: 'background-alpha-shade-55 shade-30',
    isActive: 'background-alpha-blue-45 blue-30 blue-20-on-dark-mode',
  },
  purple: {
    base: 'font-weight-extrabold white border-small border-transparent',
    default: classNames(
      'background-purple-10 background-purple-30-on-dark-mode',
      'background-purple-05-on-hover background-purple-40-on-dark-mode-hover',
      'background-purple-05-on-active background-purple-40-on-dark-mode-active'
    ),
    disabled: 'background-alpha-shade-55 shade-30',
    isActive:
      'background-purple-05 border-purple-05 background-purple-40-on-dark-mode border-purple-40-on-dark-mode',
  },
  custom: {
    base: 'font-weight-semibold border-small border-transparent',
    default: '',
    disabled: 'background-alpha-shade-55 shade-30',
    isActive: '',
  },
} as const

export const buttonSizesClasses: Record<string, string> = {
  smaller: 'padding-vertical-2 padding-horizontal-4 line-height-small',
  small: 'padding-vertical-2 padding-horizontal-8 line-height-larger',
  medium: 'padding-vertical-8 padding-horizontal-12',
  large: 'padding-vertical-12 padding-horizontal-16',
} as const
